/**
 * New Hero Section JavaScript
 * Handles carousel, bundle selection, and interactive features
 */

(function () {
  "use strict";

  // Wait for DOM to be fully loaded
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  function init() {
    initCarousel();
    initBundleSelector();
    initPurchaseOptions();
    initCollapsibles();
    initAddToCart();
  }

  /**
   * Carousel Functionality
   */
  function initCarousel() {
    const carouselContainers = document.querySelectorAll(".carousel-main");

    carouselContainers.forEach((container) => {
      const slides = container.querySelectorAll(".carousel-slide");
      const dots = container.querySelectorAll(".carousel-dot");
      const thumbnails = document.querySelectorAll(".carousel-thumbnail");
      const prevBtn = container.querySelector(".carousel-prev");
      const nextBtn = container.querySelector(".carousel-next");

      if (slides.length === 0) return;

      let currentSlide = 0;
      let autoplayInterval;
      const autoplayDelay = 4000; // 4 seconds

      // Go to specific slide
      function goToSlide(index) {
        // Remove active class from current slide
        slides[currentSlide].classList.remove("active");
        dots[currentSlide]?.classList.remove("active");
        thumbnails[currentSlide]?.classList.remove("active");

        // Update current slide
        currentSlide = index;
        if (currentSlide >= slides.length) currentSlide = 0;
        if (currentSlide < 0) currentSlide = slides.length - 1;

        // Add active class to new slide
        slides[currentSlide].classList.add("active");
        dots[currentSlide]?.classList.add("active");
        thumbnails[currentSlide]?.classList.add("active");
      }

      // Next slide
      function nextSlide() {
        goToSlide(currentSlide + 1);
      }

      // Previous slide
      function prevSlide() {
        goToSlide(currentSlide - 1);
      }

      // Autoplay functionality
      function startAutoplay() {
        stopAutoplay();
        autoplayInterval = setInterval(nextSlide, autoplayDelay);
      }

      function stopAutoplay() {
        if (autoplayInterval) {
          clearInterval(autoplayInterval);
        }
      }

      function resetAutoplay() {
        stopAutoplay();
        startAutoplay();
      }

      // Event listeners
      if (prevBtn) {
        prevBtn.addEventListener("click", () => {
          prevSlide();
          resetAutoplay();
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener("click", () => {
          nextSlide();
          resetAutoplay();
        });
      }

      // Dots navigation
      dots.forEach((dot, index) => {
        dot.addEventListener("click", () => {
          goToSlide(index);
          resetAutoplay();
        });
      });

      // Thumbnails navigation
      thumbnails.forEach((thumbnail, index) => {
        thumbnail.addEventListener("click", () => {
          goToSlide(index);
          resetAutoplay();
        });
      });

      // Keyboard navigation
      container.addEventListener("keydown", (e) => {
        if (e.key === "ArrowLeft") {
          prevSlide();
          resetAutoplay();
        } else if (e.key === "ArrowRight") {
          nextSlide();
          resetAutoplay();
        }
      });

      // Touch/swipe support
      let touchStartX = 0;
      let touchEndX = 0;

      container.addEventListener("touchstart", (e) => {
        touchStartX = e.changedTouches[0].screenX;
        stopAutoplay();
      });

      container.addEventListener("touchend", (e) => {
        touchEndX = e.changedTouches[0].screenX;
        handleSwipe();
        startAutoplay();
      });

      function handleSwipe() {
        const swipeThreshold = 50;
        const diff = touchStartX - touchEndX;

        if (Math.abs(diff) > swipeThreshold) {
          if (diff > 0) {
            nextSlide(); // Swipe left
          } else {
            prevSlide(); // Swipe right
          }
        }
      }

      // Pause autoplay on hover
      container.addEventListener("mouseenter", stopAutoplay);
      container.addEventListener("mouseleave", startAutoplay);

      // Start autoplay
      startAutoplay();
    });
  }

  /**
   * Bundle Selector Functionality
   */
  function initBundleSelector() {
    const bundleOptions = document.querySelectorAll(".bundle-option");

    bundleOptions.forEach((option) => {
      option.addEventListener("click", function () {
        // Remove selected class from all options
        bundleOptions.forEach((opt) => opt.classList.remove("selected"));

        // Add selected class to clicked option
        this.classList.add("selected");

        // Check the radio button
        const radio = this.querySelector('input[type="radio"]');
        if (radio) {
          radio.checked = true;
        }

        // Update pricing if needed
        updatePricing(this);

        // Trigger custom event
        document.dispatchEvent(
          new CustomEvent("bundleChanged", {
            detail: {
              bundleId: this.dataset.bundleId,
              element: this,
            },
          })
        );
      });
    });
  }

  /**
   * Purchase Options Functionality
   */
  function initPurchaseOptions() {
    const purchaseOptions = document.querySelectorAll(".purchase-option");

    purchaseOptions.forEach((option) => {
      const radio = option.querySelector('input[type="radio"]');

      if (radio) {
        radio.addEventListener("change", function () {
          updatePurchaseSelection(this.value);

          // Trigger custom event
          document.dispatchEvent(
            new CustomEvent("purchaseTypeChanged", {
              detail: {
                type: this.value,
                element: option,
              },
            })
          );
        });
      }

      // Allow clicking on the entire option area
      option.addEventListener("click", function (e) {
        if (e.target.tagName !== "INPUT") {
          const radioInput = this.querySelector('input[type="radio"]');
          if (radioInput) {
            radioInput.checked = true;
            radioInput.dispatchEvent(new Event("change"));
          }
        }
      });
    });
  }

  /**
   * Collapsible Sections Functionality
   */
  function initCollapsibles() {
    const collapsibleHeaders = document.querySelectorAll(".collapsible-header");

    collapsibleHeaders.forEach((header) => {
      header.addEventListener("click", function () {
        const isExpanded = this.getAttribute("aria-expanded") === "true";
        const content = this.nextElementSibling;

        // Toggle expanded state
        this.setAttribute("aria-expanded", !isExpanded);

        // Animate content
        if (!isExpanded) {
          content.style.maxHeight = content.scrollHeight + "px";
        } else {
          content.style.maxHeight = "0";
        }

        // Trigger custom event
        document.dispatchEvent(
          new CustomEvent("collapsibleToggled", {
            detail: {
              expanded: !isExpanded,
              element: this,
            },
          })
        );
      });
    });
  }

  /**
   * Add to Cart Functionality
   */
  function initAddToCart() {
    const addToCartBtn = document.getElementById("add-to-cart-hero");

    if (addToCartBtn) {
      addToCartBtn.addEventListener("click", function (e) {
        e.preventDefault();

        // Get selected bundle
        const selectedBundle = document.querySelector(
          ".bundle-option.selected"
        );
        const selectedPurchaseType = document.querySelector(
          'input[name="purchase-type"]:checked'
        );

        if (!selectedBundle) {
          showNotification("Please select a bundle", "error");
          return;
        }

        const bundleData = {
          bundleId: selectedBundle.dataset.bundleId,
          purchaseType: selectedPurchaseType
            ? selectedPurchaseType.value
            : "one-time",
        };

        // Show loading state
        const originalText = this.innerHTML;
        this.innerHTML = "<span>Adding...</span>";
        this.disabled = true;

        // Simulate add to cart (replace with actual Shopify AJAX call)
        setTimeout(() => {
          this.innerHTML = originalText;
          this.disabled = false;
          showNotification("Added to cart successfully!", "success");

          // Trigger custom event
          document.dispatchEvent(
            new CustomEvent("productAdded", {
              detail: bundleData,
            })
          );
        }, 1000);

        // For actual Shopify implementation, use:
        // addToCart(bundleData);
      });
    }
  }

  /**
   * Update pricing based on bundle selection
   */
  function updatePricing(bundleElement) {
    const price = bundleElement.querySelector(".bundle-price")?.textContent;
    const oneTimePrice = document.querySelector(
      "#one-time + label .option-price"
    );
    const subscribePrice = document.querySelector(
      "#subscribe + label .option-price"
    );

    if (price && oneTimePrice) {
      oneTimePrice.textContent = price;
    }

    if (price && subscribePrice) {
      // Calculate subscribe price (example: 15% off)
      const priceValue = parseFloat(price.replace(/[^0-9.]/g, ""));
      const subscribeValue = (priceValue * 0.85).toFixed(2);
      const currencySymbol = price.match(/[^0-9.]/)?.[0] || "$";

      subscribePrice.innerHTML = `${currencySymbol}${subscribeValue} <span class="original-price">${price}</span>`;
    }
  }

  /**
   * Update purchase selection styling
   */
  function updatePurchaseSelection(type) {
    const options = document.querySelectorAll(".purchase-option");
    options.forEach((option) => {
      option.classList.remove("active");
    });

    const selectedOption = document
      .querySelector(`#${type}`)
      ?.closest(".purchase-option");
    if (selectedOption) {
      selectedOption.classList.add("active");
    }
  }

  /**
   * Show notification message
   */
  function showNotification(message, type = "info") {
    // Remove existing notifications
    const existingNotification = document.querySelector(".hero-notification");
    if (existingNotification) {
      existingNotification.remove();
    }

    // Create notification
    const notification = document.createElement("div");
    notification.className = `hero-notification hero-notification--${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${
        type === "success"
          ? "#4CAF50"
          : type === "error"
          ? "#f44336"
          : "#2196F3"
      };
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      z-index: 10000;
      animation: slideInRight 0.3s ease-out;
      font-weight: 600;
    `;

    document.body.appendChild(notification);

    // Remove after 3 seconds
    setTimeout(() => {
      notification.style.animation = "slideOutRight 0.3s ease-out";
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  /**
   * Add to cart via Shopify AJAX API
   * Uncomment and customize for production use
   */
  /*
  function addToCart(bundleData) {
    const formData = {
      items: [{
        id: bundleData.variantId,
        quantity: 1,
        properties: {
          '_purchase_type': bundleData.purchaseType
        }
      }]
    };
    
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
    })
    .then(response => response.json())
    .then(data => {
      showNotification('Added to cart successfully!', 'success');
      // Update cart count, etc.
    })
    .catch(error => {
      console.error('Error:', error);
      showNotification('Failed to add to cart', 'error');
    });
  }
  */

  // Add animation keyframes
  const style = document.createElement("style");
  style.textContent = `
    @keyframes slideInRight {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }
    
    @keyframes slideOutRight {
      from {
        transform: translateX(0);
        opacity: 1;
      }
      to {
        transform: translateX(100%);
        opacity: 0;
      }
    }
  `;
  document.head.appendChild(style);

  // Expose functions for external use if needed
  window.newHeroSection = {
    showNotification,
    updatePricing,
  };
})();
